import {
  PreloadController,
  SeoController,
  setProId,
  useWhatsappModal,
} from '@application/components';
import { RedirectWithStatus } from '@application/components/app/App/RedirectWithStatus';
import type { Route } from '@application/types';
import { formatLanguages, useConfirmModal, useRouter, useTranslatedUrl } from '@application/utils';
import { Analytics } from '@application/utils/analytics';
import { CountryCode, ProfessionalTypeEnum } from '@gql/graphql';
import { PrebookingWarningModal } from '@presentation/components';
import type { LabelData, PreBookingWarningData } from '@presentation/components';
import { ProPage } from '@presentation/pages';
import { PageSkeleton } from '@presentation/pages/Pro/components/PageSkeleton';
import { useTranslatableContentState } from '@application/state';
import type { LocationDescriptor } from 'history';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import defaultImagePro from '@assets/default-image-pro.webp';
import { Navigate } from 'react-router-dom';
import { BookingSlotPickerController } from '../../BookingSlotPickerController';
import { useGetPro } from './api/useGetPro';
import { useIncrementViewCount } from './api/useIncrementViewCount';
import { useLogContactInfoClick } from './api/useLogContactInfoClick';
import { useRelatedLinks } from './api/useRelatedLinks';
import type { VetPageControllerProps } from './types';
import { useFormatPro } from './api/useFormatPro';
import { useFormatPracticalInfo } from './api/useFormatPracticalInfo';
import { useFormatProSchedule } from './api/useFormatProSchedule';
import { useFormatServiceSections } from './api/useFormatServiceSections';

export const ProPageController: FC<VetPageControllerProps> = (props) => {
  const { type } = props;

  const {
    language,
    countryCode,
    translate,
    infoFromCurrentPath,
    routeLocationDescriptor,
    getLocaleLinksForTranslations,
    translatedRoute,
  } = useRouter();
  const [showOverlay, setShowOverlay] = useState(false);
  const { modalHook: whatsappModal } = useWhatsappModal();
  const {
    modal: prebookingWarningModal,
    confirmedShow: prebookingWarningConfirmedShow,
    confirmHandler: prebookingWarningConfirmHandler,
  } = useConfirmModal();

  const url = useMemo(() => {
    return infoFromCurrentPath.params[infoFromCurrentPath.params.length - 1];
  }, [infoFromCurrentPath.params]);

  const { pro, proLoading } = useGetPro(url);

  const { formattedPro, slideshow, imagesToPreload, proContentLanguage } = useFormatPro(pro, type);
  const { practicalInfo } = useFormatPracticalInfo(pro, type);
  const { proSchedule, shopSchedule, urgencyAvailabilities } = useFormatProSchedule(pro);
  const { serviceSections } = useFormatServiceSections(pro);

  const { incrementViewCount } = useIncrementViewCount();
  const { setOriginalLang } = useTranslatableContentState();

  // TC-1354
  useEffect(() => {
    if (proContentLanguage) {
      setOriginalLang(proContentLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proContentLanguage]);

  useEffect(() => {
    incrementViewCount(pro?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pro?.id]);

  const [showContactInfo, setShowContactInfo] = useState(false);
  const { relatedLinks } = useRelatedLinks({
    proType: type,
    proId: pro?.id,
    ...(pro?.address?.lat && pro?.address?.lng
      ? { nearByPoint: { lat: pro.address.lat, lng: pro.address.lng } }
      : {}),
  });

  useTranslatedUrl(pro?.translations);

  const translation = useMemo(() => {
    return pro?.translations.find((entry) => entry.language === language);
  }, [language, pro?.translations]);

  const backLink = useMemo((): LocationDescriptor => {
    const route: Route = type === ProfessionalTypeEnum.VET ? 'vetList' : 'groomerList';
    return routeLocationDescriptor(route, {
      search: infoFromCurrentPath?.state?.returnSearch ?? {},
      params: infoFromCurrentPath?.state?.returnParams ?? [],
    });
  }, [
    infoFromCurrentPath?.state?.returnParams,
    infoFromCurrentPath?.state?.returnSearch,
    routeLocationDescriptor,
    type,
  ]);

  const { logContactInfoClick } = useLogContactInfoClick();

  const showContactInfoHandler = useCallback(() => {
    if (!pro?.type || !pro?.id) {
      throw new Error('Missing pro.type or pro.id found');
    }
    setShowContactInfo(true);

    if (pro.type === ProfessionalTypeEnum.VET) {
      Analytics.sendGetVetInfosEvent();
    }

    if (pro.type === ProfessionalTypeEnum.GROOMER) {
      Analytics.sendGetGrommerInfosEvent();
    }

    logContactInfoClick({ variables: { proId: pro.id } });
  }, [logContactInfoClick, pro?.id, pro?.type]);

  const isUnclaimed = useMemo(() => {
    return pro?.unclaimedProfile ?? false;
  }, [pro?.unclaimedProfile]);

  const unclaimedLink = useMemo((): LocationDescriptor => {
    const route: Route = type === ProfessionalTypeEnum.VET ? 'joinVet' : 'joinGroomer';
    return routeLocationDescriptor(route);
  }, [routeLocationDescriptor, type]);

  const tipawPartnerRef = useRef<HTMLDivElement>(null);

  const headerTags: LabelData[] | undefined = useMemo(() => {
    const partnerTag: LabelData = {
      content: translate('glossary.credit'),
      size: 'large',
      theme: 'yellow',
      onClick: () => {
        tipawPartnerRef?.current?.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'center',
        });
      },
    };
    const languageTags: LabelData[] =
      pro?.spokenLanguages?.map((x) => ({
        content: formatLanguages(language, [x]),
        size: 'large',
        theme: 'light',
      })) ?? [];
    if (pro?.isTipawPartner) {
      return [...languageTags, partnerTag];
    }
    return languageTags;
  }, [language, tipawPartnerRef, pro?.isTipawPartner, pro?.spokenLanguages, translate]);

  const enableWhatsAppContact = useMemo(() => {
    return !!pro?.whatsappNumber;
  }, [pro?.whatsappNumber]);

  const prebookingWarning = useMemo((): PreBookingWarningData => {
    return {
      proWarning:
        pro?.translations?.find((tr) => tr.language === language)?.preBookingWarning ?? undefined,
      workerServiceWarning: undefined,
    };
  }, [language, pro?.translations]);

  const hasPrebookingWarning = useMemo(
    () => prebookingWarning.proWarning || prebookingWarning.workerServiceWarning,
    [prebookingWarning.proWarning, prebookingWarning.workerServiceWarning],
  );
  const onWhatsappClickHandler = useCallback(async () => {
    let confirmed = !hasPrebookingWarning;
    if (!confirmed) {
      confirmed = await prebookingWarningConfirmedShow?.();
    }
    if (!confirmed) return;
    Analytics.clickOnGroomerWhatsapp();
    whatsappModal.show();
  }, [hasPrebookingWarning, whatsappModal, prebookingWarningConfirmedShow]);

  useEffect(() => {
    if (pro?.id) {
      setProId(pro?.id);
    }
  }, [pro?.id]);

  const thumbnail = useMemo(() => {
    const img = (pro?.imgs ?? [])[0]?.medium;
    if (!proLoading && !img) {
      return defaultImagePro;
    }
    return img;
  }, [pro?.imgs, proLoading]);

  const shouldRedirect = useMemo(() => {
    const proCountry = pro?.address?.country;
    if (proLoading) return false;
    if (countryCode === CountryCode.EU) return false;
    return proCountry !== countryCode;
  }, [countryCode, pro?.address?.country, proLoading]);

  return (
    <>
      <PreloadController images={imagesToPreload} />
      <SeoController
        title={translation?.seoTitle ?? undefined}
        description={translation?.seoDescription ?? undefined}
        keywords={translation?.seoKeywords ?? undefined}
        thumbnail={thumbnail ?? undefined}
        localeLinks={getLocaleLinksForTranslations(pro?.translations, pro?.address?.country)}
      />
      {proLoading && <PageSkeleton backLink={backLink} />}
      {!proLoading && !pro && <Navigate replace to="/404" />}
      {shouldRedirect ? (
        <RedirectWithStatus to={translatedRoute('proLanding')} status={302} />
      ) : null}
      {!proLoading && !!pro && !!isUnclaimed && (
        <ProPage
          isUnclaimed
          unclaimedLink={unclaimedLink}
          header={{
            title: translation?.title ? translation?.title : pro?.translations[0]?.title,
            tags: headerTags,
            backLink,
          }}
          practicalInfo={practicalInfo}
          showContactInfo={showContactInfo}
          onShowContactInfo={showContactInfoHandler}
          contactInfo={formattedPro?.contactInfo}
          onOverlayClick={() => setShowOverlay(false)}
          onSideCardClick={() => setShowOverlay(true)}
          showOverlay={showOverlay}
          relatedLinks={relatedLinks}
          tipawPartnerRef={tipawPartnerRef}
          proType={formattedPro?.proType}
          proCenterType={formattedPro?.proCenterType}
          enableWhatsAppContact={enableWhatsAppContact}
          onWhatsappClick={onWhatsappClickHandler}
        />
      )}
      {!proLoading && !!pro && !isUnclaimed && (
        <ProPage
          {...formattedPro}
          header={{
            title: translation?.title ? translation?.title : pro?.translations[0]?.title,
            tags: headerTags,
            backLink,
          }}
          slideshow={slideshow}
          serviceSections={serviceSections}
          showContactInfo={showContactInfo}
          onShowContactInfo={showContactInfoHandler}
          practicalInfo={practicalInfo}
          BookingForm={<BookingSlotPickerController proUrl={url} />}
          onOverlayClick={() => setShowOverlay(false)}
          onSideCardClick={() => setShowOverlay(true)}
          showOverlay={showOverlay}
          relatedLinks={relatedLinks}
          isTipawPartner={pro?.isTipawPartner}
          tipawPartnerRef={tipawPartnerRef}
          proSchedule={proSchedule}
          shopSchedule={shopSchedule}
          urgencyAvailabilities={urgencyAvailabilities}
          enableWhatsAppContact={enableWhatsAppContact}
          onWhatsappClick={onWhatsappClickHandler}
        />
      )}
      <PrebookingWarningModal
        onConfirm={() => {
          prebookingWarningConfirmHandler();
        }}
        modal={prebookingWarningModal}
        {...prebookingWarning}
      />
    </>
  );
};
